import React from "react";

import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

import { ColumnConfig, ReportItemTable } from "../../definitions";
import { useIndicators } from "../../hooks/useIndicators";
import { useWidgetFetch } from "../../hooks/useWidgetFetch";
import { formatValues, replaceVariableExpressions } from "../../utils";
import { RendererProps } from "./types";

import "./styles.scss";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "tfoot & td, tfoot & th": {
    // backgroundColor: theme.palette.background.default,
    // border: 0,
    // fontWeight: "bold",
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(24),
  textWrap: "nowrap",
}));

interface TableRendererProps extends RendererProps {
  config: ReportItemTable;
}

// TODO: add pagination / multiple columns
export const TableWidget = (props: TableRendererProps) => {
  const { data, colNames, isLoading, error } = useWidgetFetch(
    props.config.title || props.config.id,
    props.config.dataSource,
    props.optionValues,
    props.config.columns,
  );
  const someIndicatorView = useIndicators(props, isLoading, error);
  if (someIndicatorView) {
    return someIndicatorView;
  }

  const headerCols = props.config.headerColumns;
  const cols = props.config.columns || colNames.map(name => ({ name, type: "string" }) as ColumnConfig) || [];

  const { summaryInData, summary, zebraFieldIndex, summaryRows = 1 } = props.config.options || {};

  const tableData = summaryInData ? data.slice(0, data.length - summaryRows) : data;
  const tableFooter = summaryInData ? data.slice(data.length - summaryRows) : [];

  const getRowConfig = (rowIndex: number) => {
    const defaultRowConfig = { which: 0, style: {}, cellStyle: {}, columns: cols };
    if (props.config.rowFormatter) {
      for (const rf of props.config.rowFormatter) {
        // if (rf.which === "last" && rowIndex === tableData.length - 1) {
        //   return { ...defaultRowConfig, ...rf };
        if (typeof rf.which === "number" && rf.which >= 0 && rf.which === rowIndex) {
          return { ...defaultRowConfig, ...rf };
        } else if (typeof rf.which === "number" && rf.which < 0 && -rf.which === tableData.length - rowIndex) {
          return { ...defaultRowConfig, ...rf };
        }
      }
    }
    return defaultRowConfig;
  };

  let c = false;
  const zebraColors = tableData.map((row, rowIndex) => {
    if (!zebraFieldIndex || zebraFieldIndex.some(zidx => row[zidx] !== tableData[rowIndex - 1]?.[zidx])) {
      c = !c;
    }
    return c;
  });

  return (
    <TableContainer>
      <Table size="small" className="table-widget">
        <colgroup>
          {cols.map((col, index) => (
            <col key={index} width={col?.width || col?.style?.width || "80px"} />
          ))}
        </colgroup>
        <TableHead>
          {headerCols && headerCols.length > 0 && (
            <TableRow>
              {headerCols.map((col, index) => (
                <StyledTableCell key={index} align={col?.align || "left"} style={col?.style} colSpan={col?.colSpan}>
                  {replaceVariableExpressions(col.name)}
                </StyledTableCell>
              ))}
            </TableRow>
          )}
          <TableRow>
            {cols.map((col, index) => (
              <StyledTableCell key={index} align={col?.align || "left"} style={col?.style} colSpan={col?.colSpan}>
                {replaceVariableExpressions(col.name)}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row: Record<string, any>, rowIndex: number) => (
            <StyledTableRow
              key={rowIndex}
              style={getRowConfig(rowIndex).style}
              className={zebraColors[rowIndex] ? "odd" : "even"}>
              {cols.map((col, colIndex) => (
                <StyledTableCell
                  key={colIndex}
                  align={getRowConfig(rowIndex).columns[colIndex]?.align || "left"}
                  style={getRowConfig(rowIndex).columns[colIndex]?.style || getRowConfig(rowIndex)?.cellStyle}>
                  {formatValues(row[colIndex], getRowConfig(rowIndex).columns[colIndex])}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
          {summary && (
            <StyledTableRow className="footer">
              {cols.map((_col, colIndex) => (
                <StyledTableCell key={colIndex} align={cols[colIndex]?.align || "left"} sx={cols[colIndex]?.style}>
                  {cols[colIndex].summary
                    ? formatValues(computeSummary(tableData, cols, colIndex), cols[colIndex])
                    : ""}
                </StyledTableCell>
              ))}
            </StyledTableRow>
          )}
          {summaryInData && (
            <>
              {tableFooter.map((row: Record<string, any>, rowIndex: number) => (
                <StyledTableRow className="footer" key={rowIndex} style={getRowConfig(rowIndex).style}>
                  {cols.map((value, colIndex) => (
                    <StyledTableCell key={colIndex} align={cols[colIndex]?.align || "left"} sx={cols[colIndex]?.style}>
                      {formatValues(row[colIndex], getRowConfig(rowIndex).columns[colIndex])}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const computeSummary = (tableData: any[], cols: ColumnConfig[], colIndex: number): any => {
  switch (cols[colIndex]?.summary) {
    case true:
    case "count":
      return tableData.length;
    case "distinct":
      return Array.from(new Set(tableData.map(row => row[colIndex]))).length;
    case "sum":
      return tableData.reduce((sum, row) => {
        return sum + (row[colIndex] || 0);
      }, 0);
    case "percent":
      return tableData.reduce((sum, row) => {
        const v = row[colIndex] || 1;
        return sum * v != 0 ? v : 1;
      }, 0);
    default:
      return null;
  }
};
